import { useRef, useState } from "react";
import type { Issue } from "@broadpaper/core";
import { useSession, useEditorState, useUi, useFeatures, useOptions, useLayout } from "../state/context.js";
import type { EditorActions } from "../state/actions.js";
import { Button, Segmented, Badge } from "../ui/primitives.js";
import { Menu, Popover, type MenuItem } from "../ui/floating.js";
import { Icon } from "../ui/icons.js";
import { useElementWidth } from "../ui/use-element-size.js";
import { SaveIndicator } from "./SaveIndicator.js";
import { densityFor, shows } from "./density.js";
import { fitZoom } from "./zoom.js";

export function Toolbar({ actions }: { actions: EditorActions }) {
  const session = useSession();
  const options = useOptions();
  const features = useFeatures();
  const mode = useEditorState((s) => s.mode);
  const zoom = useEditorState((s) => s.zoom);
  const version = useEditorState((s) => s.version);
  const name = useEditorState((s) => s.template.name);
  const issues = useUi((u) => u.issues);
  const exporting = useUi((u) => u.exporting);
  const activeThemeId = useUi((u) => u.activeThemeId);
  const activeSampleId = useUi((u) => u.activeSampleId);
  const layout = useLayout();
  const endRef = useRef<HTMLDivElement>(null);
  const startRef = useRef<HTMLDivElement>(null);
  const density = densityFor(useElementWidth(endRef));
  // The leading group has its own budget: the report name is the only thing in
  // it that can shrink, so the save wording survives long after the trailing
  // group has given up its labels.
  const startWidth = useElementWidth(startRef);
  const showSaveText = startWidth < 0 || startWidth >= 380;
  const [nameDraft, setNameDraft] = useState<string | null>(null);
  void version;

  const themes = session.themes;
  const theme = session.activeTheme;
  const samples = session.sampleSets;
  const sample = samples.find((s) => s.id === activeSampleId);
  const errors = issues.filter((i) => i.severity === "error").length;
  const warnings = issues.filter((i) => i.severity === "warning").length;
  const canUndo = session.store.canUndo();
  const canRedo = session.store.canRedo();
  const showThemes = features.themes && themes.length > 0;
  const showSamples = samples.length > 1;
  const inline = shows.pickersInline(density);
  const narrow = useUi((u) => u.narrow);

  const setZoomFit = (fit: "width" | "page") => {
    if (session.fit) return session.fit(fit);
    // No canvas mounted yet: still set a sensible zoom rather than nothing.
    const el = session.doc.querySelector<HTMLElement>(".bpe-canvas__scroll");
    if (el && layout) session.store.setZoom(fitZoom(fit, el, layout.page));
  };

  const themeItems: MenuItem[] = themes.map((t) => ({ id: t.id, label: t.name, icon: t.id === activeThemeId ? "check" : "palette", onSelect: () => session.setTheme(t.id) }));
  const sampleItems: MenuItem[] = samples.map((s) => ({ id: s.id, label: s.label, description: s.description, icon: s.id === activeSampleId ? "check" : "database", onSelect: () => session.setSample(s.id) }));

  const overflowItems: MenuItem[] = [
    ...(!inline && showThemes ? themeItems.map((i) => ({ ...i, id: `theme-${i.id}` })) : []),
    ...(!inline && showSamples ? sampleItems.map((i, n) => ({ ...i, id: `sample-${i.id}`, separatorBefore: n === 0 && showThemes })) : []),
    // Narrow, the panels are drawers over the canvas and only one fits, so one
    // item toggling both would either cover the canvas twice over or be the
    // wrong one. Wide, they are columns that sit together and a single item is
    // what people reach for.
    ...(narrow
      ? [
          {
            id: "panel-left",
            label: "Insert blocks",
            icon: "panel-left",
            separatorBefore: !inline && (showThemes || showSamples),
            onSelect: () => {
              const open = !session.ui.get().leftOpen;
              session.ui.patch({ leftOpen: open, rightOpen: false });
            }
          },
          {
            id: "panel-right",
            label: "Properties",
            icon: "panel-right",
            onSelect: () => {
              const open = !session.ui.get().rightOpen;
              session.ui.patch({ rightOpen: open, leftOpen: false });
            }
          }
        ]
      : [
          {
            id: "panels",
            label: "Show or hide the side panels",
            icon: "panel-left",
            separatorBefore: !inline && (showThemes || showSamples),
            onSelect: () => {
              const u = session.ui.get();
              const open = !(u.leftOpen && u.rightOpen);
              session.ui.patch({ leftOpen: open, rightOpen: open });
            }
          }
        ]),
    { id: "guides", label: "Show page margins", icon: "ruler", onSelect: () => session.ui.patch({ guides: !session.ui.get().guides }) }
  ];

  return (
    <header className="bpe-toolbar" role="toolbar" aria-label="Report designer toolbar">
      <div ref={startRef} className="bpe-toolbar__group bpe-toolbar__group--start">
        {!features.whiteLabel ? (
          <div className="bpe-toolbar__brand">
            {/*
              The BroadPaper mark: a sheet with its corner turned down. The
              silhouette is the logo's, without the monogram, because at 24px
              the letterforms would be mud — and because keeping the outlines
              in one place (the site's logo.json) means this cannot drift from
              them. It is the product's own attribution, so the red is
              hard-coded rather than themed; `whiteLabel` removes it entirely.
            */}
            <span className="bpe-toolbar__mark" title="Made with BroadPaper">
              <svg viewBox="0 0 44 36" role="img" aria-label="BroadPaper">
                <path d="M2 0H35L44 9V34Q44 36 42 36H2Q0 36 0 34V2Q0 0 2 0Z" fill="#d5451b" />
                <path d="M35 0V9H44Z" fill="#171511" />
              </svg>
            </span>
          </div>
        ) : null}
        <input
          className="bpe-toolbar__name"
          aria-label="Report name"
          title={name}
          value={nameDraft ?? name}
          onChange={(e) => setNameDraft(e.target.value)}
          onBlur={() => {
            if (nameDraft !== null && nameDraft.trim() && nameDraft !== name) session.store.dispatch({ type: "setTemplateName", name: nameDraft.trim() });
            setNameDraft(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") (e.target as HTMLInputElement).blur();
            if (e.key === "Escape") setNameDraft(null);
          }}
        />
        <SaveIndicator compact={!showSaveText} canSave={!!options.onSave} onSave={() => actions.save()} />
        <span className="bpe-toolbar__sep" />
        <Button variant="ghost" icon="undo" tooltip={canUndo ? `Undo ${session.store.undoLabel() ?? ""}` : "Nothing to undo"} shortcut="⌘Z" disabled={!canUndo} onClick={() => actions.undo()} aria-label="Undo" />
        <Button variant="ghost" icon="redo" tooltip={canRedo ? `Redo ${session.store.redoLabel() ?? ""}` : "Nothing to redo"} shortcut="⇧⌘Z" disabled={!canRedo} onClick={() => actions.redo()} aria-label="Redo" />
      </div>

      <div className="bpe-toolbar__group bpe-toolbar__group--centre">
        <Segmented
          className="bpe-mode"
          /* Icon-only narrow: the two labels cost about ninety pixels the
             centre row does not have at 360px, and `iconOnly` keeps the
             accessible name on `aria-label`, so Design and Preview are
             still what a screen reader and a test find. */
          iconOnly={narrow}
          value={mode}
          onChange={(m) => session.setMode(m as "design" | "preview")}
          ariaLabel="Editor mode"
          exclusive
          options={[
            { value: "design", label: "Design", icon: "pencil" },
            { value: "preview", label: "Preview", icon: "eye" }
          ]}
        />
        <span className="bpe-toolbar__sep" />
        <div className="bpe-zoom">
          <Button variant="ghost" icon="zoom-out" tooltip="Zoom out" shortcut="⌘−" onClick={() => session.store.setZoom(zoom - 0.1)} aria-label="Zoom out" />
          <Menu
            label="Zoom"
            trigger={<button type="button" className="bpe-btn bpe-btn--ghost bpe-zoom__value" aria-label={`Zoom: ${Math.round(zoom * 100)} per cent`}>{Math.round(zoom * 100)}%</button>}
            items={[
              { id: "fit-page", label: "Fit page", icon: "maximize", onSelect: () => setZoomFit("page") },
              { id: "fit-width", label: "Fit width", icon: "move-horizontal", onSelect: () => setZoomFit("width") },
              ...[50, 75, 100, 125, 150, 200].map((z) => ({ id: String(z), label: `${z}%`, hint: z === 100 ? "⌘0" : undefined, separatorBefore: z === 50, onSelect: () => session.store.setZoom(z / 100) }))
            ]}
          />
          <Button variant="ghost" icon="zoom-in" tooltip="Zoom in" shortcut="⌘+" onClick={() => session.store.setZoom(zoom + 0.1)} aria-label="Zoom in" />
          <Button variant="ghost" icon="move-horizontal" tooltip="Fit width" onClick={() => setZoomFit("width")} aria-label="Fit width" />
          <Button variant="ghost" icon="maximize" tooltip="Fit page" onClick={() => setZoomFit("page")} aria-label="Fit page" />
        </div>
      </div>

      <div ref={endRef} className="bpe-toolbar__group bpe-toolbar__group--end">
        {showThemes && inline ? (
          <Menu
            label="Brand theme"
            placement="bottom-end"
            trigger={
              <button type="button" className="bpe-theme-select" aria-label={`Brand theme: ${theme.name}`} title={`Brand theme: ${theme.name}`}>
                <span className="bpe-swatches" aria-hidden="true">
                  <span className="bpe-swatch" style={{ background: theme.colors.primary }} />
                  <span className="bpe-swatch" style={{ background: theme.colors.secondary }} />
                  <span className="bpe-swatch" style={{ background: theme.colors.accent }} />
                </span>
                {shows.themeName(density) ? <span className="bpe-truncate bpe-theme-select__name">{theme.name}</span> : null}
                <Icon name="chevron-down" size={12} />
              </button>
            }
            items={themeItems}
          />
        ) : null}
        {showSamples && inline ? (
          <Menu
            label="Sample data"
            placement="bottom-end"
            trigger={
              <Button variant="ghost" icon="database" tooltip={`Sample data: ${sample?.label ?? "none"}`} aria-label={`Sample data: ${sample?.label ?? "none"}`}>
                {shows.sampleLabel(density) ? sample?.label ?? "Sample data" : null}
              </Button>
            }
            items={sampleItems}
          />
        ) : null}
        {features.validation ? <IssuesPopover issues={issues} errors={errors} warnings={warnings} /> : null}
        <Menu
          label="More options"
          placement="bottom-end"
          trigger={<Button variant="ghost" icon="more" tooltip="More options" aria-label="More options" />}
          items={overflowItems}
        />
        <span className="bpe-toolbar__sep" />
        {options.onSave ? <Button icon="save" tooltip="Save" shortcut="⌘S" onClick={() => actions.save()} aria-label="Save">{shows.saveText(density) ? "Save" : null}</Button> : null}
        {features.pdfExport ? (
          <Menu
            placement="bottom-end"
            label="Export"
            trigger={<Button variant="primary" icon={exporting ? "clock" : "download"} disabled={exporting} aria-label={exporting ? "Exporting" : "Export"}>{exporting ? (shows.exportText(density) ? "Exporting…" : null) : shows.exportText(density) ? "Export" : null}</Button>}
            items={[
              { id: "pdf", label: options.pdfService?.url || options.onExportPdf ? "Download PDF" : "Print / Save as PDF", icon: "file-output", hint: layout ? `${layout.paged.totalPages} pages` : undefined, onSelect: () => actions.exportPdf() },
              ...(options.pdfService?.url || options.onExportPdf ? [{ id: "print", label: "Print…", icon: "printer", onSelect: () => actions.print() }] : []),
              ...(features.jsonView ? [{ id: "json", label: "Copy template JSON", icon: "braces", separatorBefore: true, onSelect: () => { navigator.clipboard?.writeText(JSON.stringify(session.store.template, null, 2)); session.toast("Template JSON copied", "success"); } }] : [])
            ]}
          />
        ) : null}
      </div>
    </header>
  );
}

/* ------------------------------------------------------------------ */

/**
 * Two lists, not one. "Your template has a broken binding" and "this client has
 * no phone number" are different problems with different owners, and a single
 * count that mixes them tells you neither. The old panel reported "0 validation
 * issues" over a report that said "Values as at ." — technically true, and
 * exactly the wrong thing to have been counting.
 */
function IssuesPopover({ issues, errors, warnings }: { issues: Issue[]; errors: number; warnings: number }) {
  const session = useSession();
  const template = issues.filter((i) => (i.source ?? "template") === "template");
  const output = issues.filter((i) => i.source === "output");
  const label = errors ? `${errors} error${errors === 1 ? "" : "s"}` : warnings ? `${warnings} warning${warnings === 1 ? "" : "s"}` : "no issues";

  return (
    <Popover
      placement="bottom-end"
      className="bpe-picker bpe-issues"
      trigger={
        <button type="button" className="bpe-btn bpe-btn--ghost" aria-label={`Checks: ${label}`} title={`Checks: ${label}`}>
          <Icon name={errors ? "alert-circle" : warnings ? "alert-triangle" : "check-circle"} style={{ color: errors ? "var(--bpe-danger)" : warnings ? "var(--bpe-warning)" : "var(--bpe-success)" }} />
          {errors || warnings ? <Badge tone={errors ? "danger" : "warning"}>{errors || warnings}</Badge> : null}
        </button>
      }
    >
      {(close) => (
        <div className="bpe-picker__list" style={{ maxHeight: 420 }}>
          <IssueGroup
            title="Template"
            blurb="Problems with the design itself, true for every report it produces."
            emptyTitle="Template checks out."
            emptyBody="Bindings, blocks and theme tokens are all valid."
            issues={template}
            close={close}
            session={session}
          />
          <IssueGroup
            title="This data"
            blurb="What the selected sample does to the output. Switch samples to check another client."
            emptyTitle="Nothing missing."
            emptyBody="Every bound field has a value in this data set."
            issues={output}
            close={close}
            session={session}
          />
        </div>
      )}
    </Popover>
  );
}

function IssueGroup({ title, blurb, emptyTitle, emptyBody, issues, close, session }: { title: string; blurb: string; emptyTitle: string; emptyBody: string; issues: Issue[]; close: () => void; session: ReturnType<typeof useSession> }) {
  return (
    <div className="bpe-issues__group">
      <div className="bpe-issues__head">
        <span className="bpe-issues__title">{title}</span>
        <Badge tone={issues.some((i) => i.severity === "error") ? "danger" : issues.length ? "warning" : "success"}>{issues.length}</Badge>
      </div>
      <p className="bpe-issues__blurb">{blurb}</p>
      {issues.length === 0 ? (
        <div className="bpe-issues__ok">
          <Icon name="check-circle" size={13} />
          <span><strong>{emptyTitle}</strong> {emptyBody}</span>
        </div>
      ) : null}
      {issues.map((i, k) => {
        const node = i.nodeId ? session.store.getNode(i.nodeId) : undefined;
        const where = node ? session.registry.get(node.type)?.name ?? node.type : i.nodeId;
        return (
          <button
            key={k}
            type="button"
            className={`bpe-issue bpe-issue--${i.severity}`}
            disabled={!i.nodeId}
            onClick={() => {
              if (!i.nodeId) return;
              session.store.select([i.nodeId]);
              session.reveal(i.nodeId);
              close();
            }}
          >
            <Icon name={i.severity === "error" ? "alert-circle" : i.severity === "warning" ? "alert-triangle" : "info"} />
            <span>
              {i.message}
              {i.nodeId ? <span className="bpe-issue__meta">{where}{i.prop ? ` · ${i.prop}` : ""} · click to open</span> : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}
